import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import {
  cmdbFormQuery,
  cmdbOwnerOptions,
  cmdbSave,
  type CiField,
  type CiFormPage,
  type Person,
  type PostValueMap,
} from '../api/cmdb'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { CheckField, FormRow, HalfField } from '../ui/FormGrid'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import './CiFormModal.css'

/**
 * `Cmdb::form` + `Cmdb::save`, in modalul din care le deschidea `cmdb.js`.
 *
 * `id` zero = adaugare. `classId` peste zero vine din registrele de o singura
 * clasa (domenii, certificate) si stramteaza lista de clase la aia una: cine a
 * apasat "Add" acolo vrea un domeniu, iar o lista din care ar putea alege gresit
 * e o lista care asteapta sa fie aleasa gresit.
 *
 * PROPRIETATILE CLASEI, NUMAI LA MODIFICARE
 *
 * La creare clasa inca nu e aleasa, deci n-avem de unde sti ce campuri sa
 * desenam - la fel ca in portal. Dupa creare se merge pe fisa, de unde Edit
 * deschide acelasi formular, de data asta cu tot cu proprietati.
 */
export function CiFormModal({
  id,
  classId = 0,
  onClose,
  onSaved,
}: {
  id: number
  classId?: number
  onClose(): void
  onSaved(id: number): void
}) {
  const form = useQuery(cmdbFormQuery(id, classId))

  return (
    <Modal id="cmdbModal" open onClose={onClose} large title={id > 0 ? 'Edit configuration item' : 'Add configuration item'}>
      {/* `Modal` isi pune singur `.modal-body` in jurul continutului. */}
      {form.error ? (
        <div className="alert alert-danger">This form could not be loaded.</div>
      ) : !form.data ? (
        <Spinner />
      ) : (
        <Body page={form.data} id={id} onClose={onClose} onSaved={onSaved} />
      )}
    </Modal>
  )
}

function Body({
  page,
  id,
  onClose,
  onSaved,
}: {
  page: CiFormPage
  id: number
  onClose(): void
  onSaved(id: number): void
}) {
  const ci = page.ci
  const [name, setName] = useState(ci?.name ?? '')
  const [ciClassId, setCiClassId] = useState(ci?.classId ?? (page.classes[0]?.id ?? 0))
  /* Clientul propus: al elementului la modificare, altfel cel ales in antet
     (adm_selected_company); daca lista are un singur client, acela. */
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  const offered = page.companies.map((c) => c.id)
  const preselect = ci?.companyId ?? (shell.data?.company.id ?? 0)
  const [companyId, setCompanyId] = useState(ci?.companyId ?? 0)
  useEffect(() => {
    if (ci !== null || companyId !== 0) return
    if (offered.includes(preselect)) setCompanyId(preselect)
    else if (offered.length === 1) setCompanyId(offered[0]!)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect, page.companies.length])
  const [status, setStatus] = useState(ci?.status ?? 1)
  const [criticality, setCriticality] = useState<string>(ci?.criticality === null || ci === null ? '' : String(ci.criticality))
  const [environment, setEnvironment] = useState(ci?.environment ?? 0)
  const [owner, setOwner] = useState(ci?.ownerUserId ?? 0)
  const [technical, setTechnical] = useState(ci?.technicalOwnerId ?? 0)
  const [contacts, setContacts] = useState<Person[]>(page.contacts)
  const [fields, setFields] = useState<Record<number, string>>(() =>
    initialFields(page.fields.filter((field) => !drawnAbove(field))),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
   * Cand se schimba clientul, se schimba si lista de oameni.
   *
   * Contactele sunt cele ale CLIENTULUI elementului, nu toate ale furnizorului:
   * o lista cu toti oamenii tuturor clientilor ar fi insemnat sa se poata pune
   * proprietarul unei firme pe elementul alteia, si numele lui s-ar fi vazut
   * acolo.
   */
  useEffect(() => {
    if (companyId < 1) {
      setContacts([])
      return
    }
    let live = true
    void cmdbOwnerOptions(companyId)
      .then((result) => {
        if (live) setContacts(result.people)
      })
      .catch(() => {
        if (live) setContacts([])
      })
    return () => {
      live = false
    }
  }, [companyId])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (name.trim() === '') {
      setError('A name is required.')
      return
    }
    setSaving(true)
    setError(null)
    const payload: PostValueMap = {
      ID: id,
      name,
      ci_class_id: ciClassId,
      company_id: companyId,
      status,
      criticality,
      environment,
      owner_user_id: owner,
      technical_owner_id: technical,
    }
    /* `field[<id>]` - aceeasi cheie pe care o astepta `Cmdb::saveFields()`. */
    for (const [fieldId, value] of Object.entries(fields)) {
      payload[`field[${fieldId}]`] = value
    }
    void cmdbSave(payload)
      .then((result) => {
        setSaving(false)
        if (result.status !== 'OK') {
          setError(result.message)
          return
        }
        toast(result.message, 'CMDB')
        onSaved(result.id ?? id)
      })
      .catch((cause: unknown) => {
        setSaving(false)
        setError(cause instanceof ApiError && cause.message ? cause.message : 'That could not be saved.')
      })
  }

  /* Clientul nu se poate muta pe un element venit din descoperire: acolo el
     urmeaza echipamentul. */
  const companyLocked = ci !== null && !ci.manual
  /* Cmdb::form / form.php: ecranul din care s-a venit (registrul Domains) stie
     deja clientul - casuta lui nu se mai deseneaza, valoarea pleaca ascunsa. */
  const hiddenCompany = page.hideCompany === true && !companyLocked && companyId > 0

  /* Proprietatile clasei, fara cele pe care formularul le deseneaza deja sus -
     vezi `drawnAbove`. Tot ele sunt si cele trimise: una ascunsa nu pleaca
     deloc, deci valoarea ei ramane neatinsa. */
  const classFields = page.fields.filter((field) => !drawnAbove(field))

  /* Campurile elementului, in ordinea din `form.php`, cate doua pe rand. */
  const core: ReactNode[] = [
    <HalfField key="name" label="Name" required htmlFor="ciName">
      <input
        type="text"
        className="form-control"
        id="ciName"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
    </HalfField>,
    <HalfField key="class" label="Class" htmlFor="ciClass">
      {/* Clasa nu se poate schimba la modificare: ar orfelini fiecare
          valoare scrisa pe sablonul vechi. */}
      <select
        className="form-control"
        id="ciClass"
        value={ciClassId}
        disabled={ci !== null}
        onChange={(e) => setCiClassId(Number(e.target.value))}
      >
        {page.classes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </HalfField>,
  ]
  if (!hiddenCompany) {
    core.push(
      <HalfField key="company" label="Customer" htmlFor="ciCompany">
        <select
          className="form-control"
          id="ciCompany"
          value={companyId}
          disabled={companyLocked}
          onChange={(e) => {
            setCompanyId(Number(e.target.value))
            setOwner(0)
          }}
        >
          <option value={0}>&mdash;</option>
          {page.companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </HalfField>,
    )
  }
  core.push(
    <HalfField key="status" label="Status" htmlFor="ciStatus">
      <select className="form-control" id="ciStatus" value={status} onChange={(e) => setStatus(Number(e.target.value))}>
        {page.statuses.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </HalfField>,
    <HalfField key="criticality" label="Importance" htmlFor="ciCriticality">
      <select
        className="form-control"
        id="ciCriticality"
        value={criticality}
        onChange={(e) => setCriticality(e.target.value)}
      >
        <option value="">not set</option>
        {page.criticality.map((c) => (
          <option key={c.value} value={String(c.value)}>
            {c.label}
          </option>
        ))}
      </select>
    </HalfField>,
    <HalfField key="environment" label="Environment" htmlFor="ciEnvironment">
      <select
        className="form-control"
        id="ciEnvironment"
        value={environment}
        onChange={(e) => setEnvironment(Number(e.target.value))}
      >
        <option value={0}>Not known</option>
        {page.environments.map((env) => (
          <option key={env.value} value={env.value}>
            {env.label}
          </option>
        ))}
      </select>
    </HalfField>,
    <HalfField key="owner" label="Owner" htmlFor="ciOwner">
      <select className="form-control" id="ciOwner" value={owner} onChange={(e) => setOwner(Number(e.target.value))}>
        <option value={0}>&mdash;</option>
        {contacts.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </HalfField>,
  )
  if (page.hasTechnical) {
    core.push(
      <HalfField key="technical" label="Technical owner" htmlFor="ciTechnical">
        {/* Alt tabel decat Owner: aici e omul de la FURNIZOR care tine
            lucrul in picioare, acolo omul de la CLIENT care decide. */}
        <select
          className="form-control"
          id="ciTechnical"
          value={technical}
          onChange={(e) => setTechnical(Number(e.target.value))}
        >
          <option value={0}>&mdash;</option>
          {page.staff.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </HalfField>,
    )
  }

  /*
   * Fara `.modal-body` si aici: `Modal` isi pune deja unul in jurul
   * continutului. Cu al doilea inauntru, marginea se dubla (30px in loc de 15)
   * si linia de deasupra butoanelor se oprea la 15px de marginile ferestrei.
   * Subsolul iese peste marginile corpului, ca la `WindowFormModal`.
   */
  return (
    <form id="cmdbForm" onSubmit={submit}>
      {error !== null && <div className="alert alert-danger">{error}</div>}
      {hiddenCompany && <input type="hidden" name="company_id" value={companyId} />}
      {pairs(core.map((node) => ({ node, wide: false })))}
      {classFields.length > 0 && (
        <>
          <hr />
          {/* `form.php`: de aici in jos campurile sunt ale CLASEI. */}
          <h5 style={{ marginBottom: 12 }}>
            Properties <span className="text-muted small">of this class</span>
          </h5>
          {pairs(
            classFields.map((field) => ({
              node: (
                <FieldInput
                  key={field.id}
                  field={field}
                  value={fields[field.id] ?? ''}
                  onChange={(value) => setFields((prev) => ({ ...prev, [field.id]: value }))}
                />
              ),
              wide: field.typeLabel === 'textarea',
            })),
          )}
        </>
      )}
      <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
        <button type="button" className="btn btn-default" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          Save
        </button>
      </div>
    </form>
  )
}

/**
 * Proprietatea de clasa care e DEJA pe formular, ca rubrica a elementului.
 *
 * Clasele de echipamente (Server, Virtual Server, Network, Storage...) poarta
 * din catalogul `Services/Cmdb/Taxonomy` un camp de clasa "Environment"
 * (`field_key` = `environment`: Production / Test / Development / DR), tinut
 * in `it_ci_field_values`. Formularul are insa si rubrica elementului,
 * `it_ci.environment` - cea pe care o citesc lista si filtrul ei, State,
 * impactul si sanatatea. Desenate amandoua, omul vedea "Environment" de doua
 * ori, cu doua valori care nu stiu una de alta, fara sa poata sti pe care o
 * citeste restul CMDB-ului.
 *
 * Ramane una, rubrica elementului. Campul de clasa nu se mai deseneaza si nu
 * se mai TRIMITE: o valoare scrisa deja in el ramane cum e, nu se sterge la
 * prima salvare (un camp trimis gol isi sterge valoarea).
 */
function drawnAbove(field: CiField): boolean {
  return field.key.trim().toLowerCase() === 'environment' || field.label.trim().toLowerCase() === 'environment'
}

/**
 * Celulele, cate doua pe un `FormRow`; una lata (`textarea`) sta singura pe
 * randul ei. `FormRow` poarta `od-form-row`, fara de care grila nu se vede in
 * randul desfasurat al listei Managed: acolo invelisul tabelului e
 * `form-inline`, si Bootstrap face din fiecare `.form-group` si
 * `.form-control` un `inline-block` - vezi `ui/FormGrid.tsx`.
 */
function pairs(cells: { node: ReactNode; wide: boolean }[]): ReactNode[] {
  const rows: ReactNode[] = []
  let pending: ReactNode[] = []
  const flush = () => {
    if (pending.length > 0) rows.push(<FormRow key={rows.length}>{pending}</FormRow>)
    pending = []
  }
  for (const cell of cells) {
    if (cell.wide) {
      flush()
      pending = [cell.node]
      flush()
      continue
    }
    pending.push(cell.node)
    if (pending.length === 2) flush()
  }
  flush()
  return rows
}

function initialFields(fields: CiField[]): Record<number, string> {
  const out: Record<number, string> = {}
  for (const field of fields) {
    out[field.id] = field.value === null || field.value === undefined ? '' : String(field.value)
  }
  return out
}

/** Un camp de clasa, dupa felul lui - `Ci_fields::TYPE_*`. O celula a grilei. */
function FieldInput({ field, value, onChange }: { field: CiField; value: string; onChange(value: string): void }) {
  const id = 'ciField' + field.id
  if (field.typeLabel === 'boolean') {
    return <CheckField id={id} label={field.label} checked={value === '1'} onChange={(on) => onChange(on ? '1' : '0')} />
  }
  if (field.typeLabel === 'select') {
    return (
      <HalfField label={field.label} required={field.required} htmlFor={id}>
        <select className="form-control" id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">&mdash;</option>
          {field.options.map((option) => (
            <option key={option.id} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </HalfField>
    )
  }
  if (field.typeLabel === 'textarea') {
    return (
      <HalfField label={field.label} required={field.required} htmlFor={id} span={12}>
        <textarea className="form-control" id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
      </HalfField>
    )
  }
  const type = field.typeLabel === 'date' ? 'date' : field.typeLabel === 'number' ? 'number' : 'text'
  return (
    <HalfField label={field.label} required={field.required} htmlFor={id}>
      <input type={type} className="form-control" id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </HalfField>
  )
}
